/**
 * ActiveOperations - Real-time drilling activity insights
 * 
 * Shows VERIFIED DATA from NM OCD:
 * - Top operators by active well count
 * - Hot zones (operator activity clusters)
 * - Active well density by area
 * 
 * This replaces the "Rigs" feature with real, verifiable data
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Card,
  CardContent,
  LinearProgress,
  Divider,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert
} from '@mui/material';
import CyberSpinner from './CyberSpinner';
import { telegram } from '../services/telegram';
import { useAppStore } from '../stores/appStore';
import { getCyberColors } from '../theme/cyberColors';
import { useAutoDismiss } from '../hooks/useAutoDismiss';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VerifiedIcon from '@mui/icons-material/Verified';
import OilBarrelIcon from '@mui/icons-material/OilBarrel';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import NavigationIcon from '@mui/icons-material/Navigation';
import CategoryIcon from '@mui/icons-material/Category';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FactoryIcon from '@mui/icons-material/Factory';
import TerrainIcon from '@mui/icons-material/Terrain';
import TimelineIcon from '@mui/icons-material/Timeline';
import BlockIcon from '@mui/icons-material/Block';
import EventIcon from '@mui/icons-material/Event';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UpdateIcon from '@mui/icons-material/Update';
import BuildIcon from '@mui/icons-material/Build';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

// Extended theme colors for Active Operations component
const getOperationsColors = (darkMode: boolean, highContrast: boolean) => {
  const cyber = getCyberColors(darkMode, highContrast);
  return {
    ...cyber,
    // Component-specific extensions
    primary: cyber.neonCyan,
    secondary: cyber.neonPurple,
    success: cyber.neonGreen,
    warning: cyber.neonYellow,
    danger: cyber.neonRed,
    dark: cyber.bgPrimary,
    cardBg: cyber.bgCard,
    text: cyber.textSecondary,
    muted: cyber.textMuted,
    borderColor: cyber.borderDefault,
    borderLight: cyber.borderHover,
    hoverBg: darkMode 
      ? (highContrast ? 'rgba(34, 211, 238, 0.2)' : 'rgba(0, 212, 255, 0.15)') 
      : (highContrast ? 'rgba(22, 78, 99, 0.15)' : 'rgba(2, 132, 199, 0.1)'),
    chipBg: darkMode 
      ? (highContrast ? 'rgba(74, 222, 128, 0.2)' : 'rgba(0, 255, 136, 0.15)') 
      : (highContrast ? 'rgba(20, 83, 45, 0.2)' : 'rgba(22, 163, 74, 0.15)'),
    chipBgAlt: darkMode 
      ? (highContrast ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)') 
      : (highContrast ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.06)'),
    chipBgPrimary: darkMode 
      ? (highContrast ? 'rgba(34, 211, 238, 0.2)' : 'rgba(0, 212, 255, 0.15)') 
      : (highContrast ? 'rgba(22, 78, 99, 0.2)' : 'rgba(2, 132, 199, 0.15)'),
    chipBgSecondary: darkMode 
      ? (highContrast ? 'rgba(192, 132, 252, 0.2)' : 'rgba(255, 0, 255, 0.15)') 
      : (highContrast ? 'rgba(88, 28, 135, 0.2)' : 'rgba(168, 85, 247, 0.15)'),
    chipBgWarning: darkMode 
      ? (highContrast ? 'rgba(254, 240, 138, 0.2)' : 'rgba(255, 187, 0, 0.15)') 
      : (highContrast ? 'rgba(113, 63, 18, 0.2)' : 'rgba(217, 119, 6, 0.15)'),
    alertBg: darkMode 
      ? (highContrast ? 'rgba(34, 211, 238, 0.15)' : 'rgba(0, 212, 255, 0.1)') 
      : (highContrast ? 'rgba(22, 78, 99, 0.15)' : 'rgba(2, 132, 199, 0.1)'),
    alertBorder: darkMode 
      ? (highContrast ? 'rgba(34, 211, 238, 0.5)' : 'rgba(0, 212, 255, 0.3)') 
      : (highContrast ? 'rgba(22, 78, 99, 0.5)' : 'rgba(2, 132, 199, 0.3)'),
    errorBg: darkMode 
      ? (highContrast ? 'rgba(248, 113, 113, 0.15)' : 'rgba(255, 51, 102, 0.1)') 
      : (highContrast ? 'rgba(127, 29, 29, 0.15)' : 'rgba(220, 38, 38, 0.1)'),
    gradientPrimary: darkMode 
      ? (highContrast 
          ? 'linear-gradient(135deg, rgba(34,211,238,0.15) 0%, rgba(192,132,252,0.15) 100%)'
          : 'linear-gradient(135deg, rgba(0,212,255,0.1) 0%, rgba(255,0,255,0.1) 100%)')
      : (highContrast 
          ? 'linear-gradient(135deg, rgba(22,78,99,0.15) 0%, rgba(88,28,135,0.15) 100%)'
          : 'linear-gradient(135deg, rgba(2,132,199,0.1) 0%, rgba(168,85,247,0.1) 100%)'),
    gradientRank: darkMode
      ? (highContrast ? 'linear-gradient(135deg, #22d3ee, #c084fc)' : 'linear-gradient(135deg, #00d4ff, #ff00ff)')
      : (highContrast ? 'linear-gradient(135deg, #164e63, #581c87)' : 'linear-gradient(135deg, #0284c7, #a855f7)'),
    boxShadow: darkMode 
      ? (highContrast ? '0 0 0 2px rgba(255, 255, 255, 0.2)' : '0 8px 20px rgba(0, 212, 255, 0.2)')
      : (highContrast ? '0 0 0 2px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.1)'),
    boxShadowHover: darkMode
      ? (highContrast ? '0 0 0 2px rgba(255, 255, 255, 0.3)' : '0 14px 32px rgba(0, 212, 255, 0.35)')
      : (highContrast ? '0 0 0 2px rgba(0, 0, 0, 0.2)' : '0 8px 20px rgba(0, 0, 0, 0.15)'),
    boxShadowSecondary: darkMode
      ? (highContrast ? '0 0 0 2px rgba(192, 132, 252, 0.3)' : '0 8px 20px rgba(255, 0, 255, 0.2)')
      : (highContrast ? '0 0 0 2px rgba(88, 28, 135, 0.2)' : '0 4px 12px rgba(168, 85, 247, 0.15)'),
    boxShadowSecondaryHover: darkMode
      ? (highContrast ? '0 0 0 2px rgba(192, 132, 252, 0.4)' : '0 14px 32px rgba(255, 0, 255, 0.35)')
      : (highContrast ? '0 0 0 2px rgba(88, 28, 135, 0.3)' : '0 8px 20px rgba(168, 85, 247, 0.25)'),
    infoBg: darkMode 
      ? (highContrast ? 'rgba(34, 211, 238, 0.15)' : 'rgba(0, 212, 255, 0.1)') 
      : (highContrast ? 'rgba(22, 78, 99, 0.15)' : 'rgba(2, 132, 199, 0.1)'),
  };
};

interface OperatorStats {
  operator: string;
  activeWells: number;
  totalWells: number;
  pluggedWells: number;
  cancelledWells: number;
  activePercentage: number;
  counties: string[];
  primaryCounty: string;
  wellTypes: Record<string, number>;
}

interface CountyStats {
  county: string;
  activeWells: number;
  totalWells: number;
  pluggedWells: number;
  topOperators: { operator: string; count: number }[];
  density: number;
}

interface HotZone {
  operator: string;
  county: string;
  centerLat: number;
  centerLon: number;
  wellCount: number;
  wells: {
    id: string;
    name: string;
    api: string;
    lat: number;
    lon: number;
    status: string;
    type: string;
  }[];
  boundingBox: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

interface WellTypeStats {
  county: string;
  totalWells: number;
  totalActive: number;
  types: Record<string, { total: number; active: number }>;
  primaryType: string;
  oilPercentage: number;
  gasPercentage: number;
  injectionPercentage: number;
}

interface NewPermit {
  id: string;
  apiNumber: string;
  name: string;
  latitude: number;
  longitude: number;
  county: string;
  operator: string;
  wellType: string;
  district: string;
}

interface PermitSummary {
  county: string;
  permitCount: number;
  operators: string[];
}

interface DistrictStats {
  district: string;
  totalWells: number;
  activeWells: number;
  pluggedWells: number;
  newPermits: number;
  oilWells: number;
  gasWells: number;
  injectionWells: number;
  operatorCount: number;
  countyCount: number;
  counties: string[];
  topOperators: { operator: string; count: number }[];
  activePercentage: number;
  primaryType: string;
  oilGasRatio: number;
}

// New interfaces for additional insights
interface InfrastructureSummary {
  category: string;
  total: number;
  active: number;
  inactive: number;
}

interface InfrastructureByDistrict {
  district: string;
  tankBatteries: number;
  compressorStations: number;
  gasPlants: number;
  flareStacks: number;
  total: number;
}

interface InfrastructureOperator {
  operatorName: string;
  facilityCount: number;
  tankBatteries: number;
  compressorStations: number;
}

interface SampleFacility {
  id: string;
  name: string;
  type: string;
  operator: string;
  district: string;
  latitude: number;
  longitude: number;
  status: string;
}

interface InfrastructureStats {
  summary: InfrastructureSummary[];
  byDistrict: InfrastructureByDistrict[];
  topOperators: InfrastructureOperator[];
  sampleFacilities: SampleFacility[];
}

interface LandTypeOverall {
  landType: string;
  totalWells: number;
  activeWells: number;
  pluggedWells: number;
}

interface LandTypeByCounty {
  county: string;
  federal: number;
  state: number;
  fee: number;
  total: number;
  federalPct: number;
  statePct: number;
  feePct: number;
}

interface LandTypeOperator {
  operator: string;
  federalWells?: number;
  activeFederal?: number;
  stateWells?: number;
  activeState?: number;
}

interface LandTypeStats {
  overall: LandTypeOverall[];
  byCounty: LandTypeByCounty[];
  topFederalOperators: LandTypeOperator[];
  topStateOperators: LandTypeOperator[];
}

interface HorizontalOverall {
  wellOrientation: string;
  totalWells: number;
  activeWells: number;
  newPermits: number;
}

interface HorizontalByOperator {
  operator: string;
  horizontalWells: number;
  activeHorizontal: number;
}

interface HorizontalByCounty {
  county: string;
  horizontal: number;
  vertical: number;
  total: number;
  horizontalPct: number;
}

interface SampleWell {
  id: string;
  name: string;
  apiNumber: string;
  operator: string;
  county: string;
  latitude: number;
  longitude: number;
  wellType: string;
  status: string;
}

interface HorizontalWellStats {
  overall: HorizontalOverall[];
  byOperator: HorizontalByOperator[];
  byCounty: HorizontalByCounty[];
  sampleWells: SampleWell[];
}

interface PluggedOverall {
  status: string;
  count: number;
}

interface PluggedByOperator {
  operator: string;
  released: number;
  notReleased: number;
  totalPlugged: number;
  totalWells: number;
  plugRate: number;
  releaseRate: number;
}

interface PluggedByCounty {
  county: string;
  released: number;
  notReleased: number;
  totalPlugged: number;
  releaseRate: number;
}

interface UnreleasedWell {
  id: string;
  name: string;
  apiNumber: string;
  operator: string;
  county: string;
  latitude: number;
  longitude: number;
  district: string;
}

interface PluggedWellStats {
  overall: PluggedOverall[];
  byOperator: PluggedByOperator[];
  byCounty: PluggedByCounty[];
  unreleasedWells: UnreleasedWell[];
}

// Weekly Activity interfaces
interface WeeklyActivityItem {
  id: string;
  type: 'permit' | 'completion' | 'incident' | 'rig_move';
  apiNumber?: string;
  wellName: string;
  operator: string;
  county: string;
  latitude: number;
  longitude: number;
  date: string;
  formation?: string;
  depth?: number;
  status?: string;
  description?: string;
  severity?: string;
}

interface WeeklyStats {
  permits: number;
  completions: number;
  rigMovements: number;
  incidents: number;
  topOperators: { operator: string; count: number }[];
  byCounty: { county: string; permits: number; completions: number }[];
}

interface WeeklyActivityData {
  stats: WeeklyStats;
  permits: WeeklyActivityItem[];
  completions: WeeklyActivityItem[];
  incidents: WeeklyActivityItem[];
  rigs: WeeklyActivityItem[];
  weekDate: string;
}

interface ActiveOperationsProps {
  open: boolean;
  onClose: () => void;
  onNavigateToZone?: (lat: number, lon: number) => void;
}

// API base URL
const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? 'http://localhost:8788'
  : '';

export function ActiveOperations({ open, onClose, onNavigateToZone }: ActiveOperationsProps) {
  const darkMode = useAppStore((state) => state.darkMode);
  const highContrast = useAppStore((state) => state.highContrast);
  const colors = getOperationsColors(darkMode, highContrast);
  
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [operators, setOperators] = useState<OperatorStats[]>([]);
  const [counties, setCounties] = useState<CountyStats[]>([]);
  const [hotZones, setHotZones] = useState<HotZone[]>([]);
  const [wellTypes, setWellTypes] = useState<WellTypeStats[]>([]);
  const [permits, setPermits] = useState<NewPermit[]>([]);
  const [permitSummary, setPermitSummary] = useState<PermitSummary[]>([]);
  const [districts, setDistricts] = useState<DistrictStats[]>([]);
  const [infrastructure, setInfrastructure] = useState<InfrastructureStats | null>(null);
  const [landType, setLandType] = useState<LandTypeStats | null>(null);
  const [horizontal, setHorizontal] = useState<HorizontalWellStats | null>(null);
  const [plugged, setPlugged] = useState<PluggedWellStats | null>(null);
  const [weeklyActivity, setWeeklyActivity] = useState<WeeklyActivityData | null>(null);
  const [expandedOperator, setExpandedOperator] = useState<string | null>(null);
  const [expandedCounty, setExpandedCounty] = useState<string | null>(null);
  const [expandedZone, setExpandedZone] = useState<string | null>(null);
  const [expandedDistrict, setExpandedDistrict] = useState<string | null>(null);
  const [expandedWeeklySection, setExpandedWeeklySection] = useState<string>('permits');

  useAutoDismiss(error, () => setError(null));

  // Fetch data based on active tab
  const fetchData = useCallback(async (view: string) => {
    setLoading(true);
    setError(null);
    
    try {
      // Weekly activity uses a different API endpoint
      if (view === 'weekly') {
        const response = await fetch(`${API_BASE}/api/activity/weekly?action=all`);
        if (!response.ok) throw new Error('Failed to fetch weekly activity');
        
        const json = await response.json();
        if (!json.success) throw new Error(json.error || 'API error');
        
        setWeeklyActivity(json.data);
        setLoading(false);
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/operators/stats?view=${view}&limit=30`);
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const json = await response.json();
      if (!json.success) throw new Error(json.error || 'API error');
      
      switch (view) {
        case 'operators':
          setOperators(json.data);
          break;
        case 'counties':
          setCounties(json.data);
          break;
        case 'hotzones':
          setHotZones(json.data);
          break;
        case 'welltypes':
          setWellTypes(json.data);
          break;
        case 'permits':
          setPermits(json.data.permits || []);
          setPermitSummary(json.data.summary || []);
          break;
        case 'districts':
          setDistricts(json.data);
          break;
        case 'infrastructure':
          setInfrastructure(json.data);
          break;
        case 'landtype':
          setLandType(json.data);
          break;
        case 'horizontal':
          setHorizontal(json.data);
          break;
        case 'plugged':
          setPlugged(json.data);
          break;
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    if (open) {
      const views = ['operators', 'counties', 'hotzones', 'welltypes', 'permits', 'districts', 'infrastructure', 'landtype', 'horizontal', 'plugged', 'weekly'];
      fetchData(views[activeTab]);
    }
  }, [open, activeTab, fetchData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    telegram.hapticFeedback('light');
    setActiveTab(newValue);
    setExpandedOperator(null);
    setExpandedCounty(null);
    setExpandedZone(null);
    setExpandedDistrict(null);
  };

  const handleNavigateToZone = (lat: number, lon: number) => {
    if (onNavigateToZone) {
      onNavigateToZone(lat, lon);
      onClose();
    } else {
      // Fallback: Open in Google Maps
      window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
    }
  };

  const getWellTypeIcon = (type: string) => {
    const t = type?.toLowerCase() || '';
    if (t.includes('oil')) return <OilBarrelIcon sx={{ fontSize: 16, color: colors.success }} />;
    if (t.includes('gas')) return <LocalGasStationIcon sx={{ fontSize: 16, color: colors.warning }} />;
    if (t.includes('swd') || t.includes('water') || t.includes('disposal')) return <WaterDropIcon sx={{ fontSize: 16, color: colors.primary }} />;
    return <OilBarrelIcon sx={{ fontSize: 16, color: colors.muted }} />;
  };

  // Render operator card
  const renderOperatorCard = (op: OperatorStats, index: number) => {
    const isExpanded = expandedOperator === op.operator;
    const rank = index + 1;
    
    return (
      <Card
        key={op.operator}
        sx={{
          mb: 1.5,
          background: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          boxShadow: colors.boxShadow,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          transform: 'scale(1)',
          cursor: 'pointer',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: colors.boxShadowHover
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {/* Rank Badge */}
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: rank <= 3 
                  ? colors.gradientRank
                  : colors.chipBgAlt,
                color: rank <= 3 ? '#fff' : colors.muted,
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0
              }}
            >
              {rank}
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Operator name */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: colors.textPrimary,
                  fontSize: '0.95rem',
                  lineHeight: 1.3,
                  mb: 0.5
                }}
              >
                {op.operator}
              </Typography>
              
              {/* Stats row */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                <Chip
                  label={`${op.activeWells.toLocaleString()} Active`}
                  size="small"
                  sx={{
                    bgcolor: colors.chipBg,
                    color: colors.success,
                    fontWeight: 600,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label={`${op.totalWells.toLocaleString()} Total`}
                  size="small"
                  sx={{
                    bgcolor: colors.chipBgAlt,
                    color: colors.muted,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label={op.primaryCounty}
                  size="small"
                  icon={<LocationOnIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    bgcolor: colors.chipBgPrimary,
                    color: colors.primary,
                    fontSize: '0.75rem',
                    '& .MuiChip-icon': { color: colors.primary }
                  }}
                />
              </Box>
              
              {/* Activity bar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={op.activePercentage}
                  sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    bgcolor: colors.chipBgAlt,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: op.activePercentage > 50 ? colors.success : colors.warning,
                      borderRadius: 3
                    }
                  }}
                />
                <Typography variant="caption" sx={{ color: colors.muted, minWidth: 35 }}>
                  {op.activePercentage}%
                </Typography>
              </Box>
            </Box>
            
            {/* Expand button */}
            <IconButton
              size="small"
              onClick={() => setExpandedOperator(isExpanded ? null : op.operator)}
              sx={{ color: colors.muted }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          {/* Expanded details */}
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}` }}>
              {/* Well type breakdown */}
              <Typography variant="caption" sx={{ color: colors.muted, mb: 1, display: 'block' }}>
                Well Types:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {Object.entries(op.wellTypes).map(([type, count]) => (
                  <Chip
                    key={type}
                    label={`${type}: ${count}`}
                    size="small"
                    icon={getWellTypeIcon(type)}
                    sx={{
                      bgcolor: colors.chipBgAlt,
                      color: colors.text,
                      fontSize: '0.7rem'
                    }}
                  />
                ))}
              </Box>
              
              {/* Counties */}
              <Typography variant="caption" sx={{ color: colors.muted, mb: 1, display: 'block' }}>
                Operating in: {op.counties.join(', ')}
              </Typography>
              
              {/* Status breakdown */}
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Typography variant="caption" sx={{ color: colors.success }}>
                  ✓ {op.activeWells} active
                </Typography>
                <Typography variant="caption" sx={{ color: colors.warning }}>
                  ⬤ {op.pluggedWells} plugged
                </Typography>
                <Typography variant="caption" sx={{ color: colors.danger }}>
                  ✕ {op.cancelledWells} cancelled
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // Render county card
  const renderCountyCard = (county: CountyStats) => {
    const isExpanded = expandedCounty === county.county;
    
    return (
      <Card
        key={county.county}
        sx={{
          mb: 1.5,
          background: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          boxShadow: colors.boxShadow,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          transform: 'scale(1)',
          cursor: 'pointer',
          '&:hover': { 
            transform: 'scale(1.02)',
            boxShadow: colors.boxShadowHover
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                {county.county} County
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Chip
                  label={`${county.activeWells.toLocaleString()} Active`}
                  size="small"
                  sx={{ bgcolor: colors.chipBg, color: colors.success, fontSize: '0.75rem' }}
                />
                <Chip
                  label={`${county.density.toFixed(2)}/mi²`}
                  size="small"
                  sx={{ bgcolor: colors.chipBgWarning, color: colors.warning, fontSize: '0.75rem' }}
                />
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={() => setExpandedCounty(isExpanded ? null : county.county)}
              sx={{ color: colors.muted }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}` }}>
              <Typography variant="caption" sx={{ color: colors.muted, mb: 1, display: 'block' }}>
                Top Operators:
              </Typography>
              <List dense sx={{ py: 0 }}>
                {county.topOperators.slice(0, 5).map((op, i) => (
                  <ListItem key={op.operator} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Typography variant="caption" sx={{ color: colors.primary }}>
                        #{i + 1}
                      </Typography>
                    </ListItemIcon>
                    <ListItemText
                      primary={op.operator}
                      secondary={`${op.count} wells`}
                      primaryTypographyProps={{ fontSize: '0.85rem', color: colors.textPrimary }}
                      secondaryTypographyProps={{ fontSize: '0.75rem', color: colors.muted }}
                    />
                  </ListItem>
                ))}
              </List>
              
              <Divider sx={{ my: 1, borderColor: colors.borderLight }} />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" sx={{ color: colors.text }}>
                  Total: {county.totalWells.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.warning }}>
                  Plugged: {county.pluggedWells.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // Render hot zone card
  const renderHotZoneCard = (zone: HotZone, _index: number) => {
    const isExpanded = expandedZone === `${zone.operator}-${zone.county}`;
    const zoneKey = `${zone.operator}-${zone.county}`;
    
    return (
      <Card
        key={zoneKey}
        sx={{
          mb: 1.5,
          background: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          boxShadow: colors.boxShadowSecondary,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          transform: 'scale(1)',
          cursor: 'pointer',
          '&:hover': { 
            transform: 'scale(1.02)',
            boxShadow: colors.boxShadowSecondaryHover
          }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {/* Hot zone icon */}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: colors.chipBgSecondary,
                flexShrink: 0
              }}
            >
              <WhatshotIcon sx={{ color: colors.secondary }} />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '0.95rem' }}>
                {zone.operator}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.muted }}>
                {zone.county} County
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Chip
                  label={`${zone.wellCount} Wells`}
                  size="small"
                  sx={{ bgcolor: colors.chipBgSecondary, color: colors.secondary, fontWeight: 600 }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleNavigateToZone(zone.centerLat, zone.centerLon)}
                  sx={{
                    bgcolor: colors.chipBgPrimary,
                    color: colors.primary,
                    '&:hover': { bgcolor: colors.hoverBg }
                  }}
                >
                  <NavigationIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
            
            <IconButton
              size="small"
              onClick={() => setExpandedZone(isExpanded ? null : zoneKey)}
              sx={{ color: colors.muted }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}` }}>
              <Typography variant="caption" sx={{ color: colors.muted, mb: 1, display: 'block' }}>
                Sample Wells in Zone:
              </Typography>
              <List dense sx={{ py: 0, maxHeight: 200, overflow: 'auto' }}>
                {zone.wells.slice(0, 10).map((well) => (
                  <ListItem 
                    key={well.id} 
                    sx={{ px: 0, py: 0.5 }}
                    secondaryAction={
                      <IconButton 
                        size="small" 
                        onClick={() => handleNavigateToZone(well.lat, well.lon)}
                        sx={{ color: colors.primary }}
                      >
                        <NavigationIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {getWellTypeIcon(well.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={well.name}
                      secondary={well.api}
                      primaryTypographyProps={{ fontSize: '0.8rem', color: colors.textPrimary }}
                      secondaryTypographyProps={{ fontSize: '0.7rem', color: colors.muted }}
                    />
                  </ListItem>
                ))}
              </List>
              
              {zone.wells.length > 10 && (
                <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mt: 1 }}>
                  + {zone.wells.length - 10} more wells
                </Typography>
              )}
              
              <Divider sx={{ my: 1.5, borderColor: colors.borderLight }} />
              <Typography variant="caption" sx={{ color: colors.muted }}>
                Zone Center: {zone.centerLat.toFixed(4)}°, {zone.centerLon.toFixed(4)}°
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // Render well type distribution card
  const renderWellTypeCard = (stats: WellTypeStats) => {
    const oilWidth = stats.oilPercentage;
    const gasWidth = stats.gasPercentage;
    const injWidth = stats.injectionPercentage;
    
    return (
      <Card
        key={stats.county}
        sx={{
          mb: 1.5,
          background: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          boxShadow: colors.boxShadow
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.textPrimary }}>
              {stats.county}
            </Typography>
            <Chip
              label={stats.primaryType}
              size="small"
              sx={{
                bgcolor: stats.primaryType === 'Oil' ? colors.chipBg : colors.chipBgWarning,
                color: stats.primaryType === 'Oil' ? colors.success : colors.warning,
                fontWeight: 600
              }}
            />
          </Box>
          
          <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mb: 1.5 }}>
            {stats.totalActive.toLocaleString()} active / {stats.totalWells.toLocaleString()} total wells
          </Typography>
          
          {/* Distribution bar */}
          <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: colors.chipBgAlt }}>
            {oilWidth > 0 && (
              <Box sx={{ width: `${oilWidth}%`, bgcolor: colors.success, transition: 'width 0.3s' }} />
            )}
            {gasWidth > 0 && (
              <Box sx={{ width: `${gasWidth}%`, bgcolor: colors.warning, transition: 'width 0.3s' }} />
            )}
            {injWidth > 0 && (
              <Box sx={{ width: `${injWidth}%`, bgcolor: colors.primary, transition: 'width 0.3s' }} />
            )}
          </Box>
          
          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.success }} />
              <Typography variant="caption" sx={{ color: colors.muted }}>Oil {oilWidth}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.warning }} />
              <Typography variant="caption" sx={{ color: colors.muted }}>Gas {gasWidth}%</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors.primary }} />
              <Typography variant="caption" sx={{ color: colors.muted }}>Inj/SWD {injWidth}%</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render new permit card
  const renderPermitCard = (permit: NewPermit) => {
    return (
      <Card
        key={permit.id}
        sx={{
          mb: 1,
          background: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          boxShadow: colors.boxShadow,
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.01)' }
        }}
        onClick={() => handleNavigateToZone(permit.latitude, permit.longitude)}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: colors.chipBgWarning,
                flexShrink: 0
              }}
            >
              <NewReleasesIcon sx={{ color: colors.warning, fontSize: 20 }} />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.textPrimary, fontSize: '0.85rem' }} noWrap>
                {permit.name}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.muted, display: 'block' }}>
                {permit.operator}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                <Chip label={permit.county} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: colors.chipBgPrimary, color: colors.primary }} />
                <Chip label={permit.wellType} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: colors.chipBgAlt, color: colors.muted }} />
              </Box>
            </Box>
            
            <NavigationIcon sx={{ color: colors.primary, fontSize: 20 }} />
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Render district stats card
  const renderDistrictCard = (district: DistrictStats) => {
    const isExpanded = expandedDistrict === district.district;
    
    // District descriptions
    const districtInfo: Record<string, { basin: string; color: string }> = {
      'Hobbs': { basin: 'Permian Basin (SE NM)', color: colors.success },
      'Artesia': { basin: 'Permian Basin (Central)', color: colors.primary },
      'Aztec': { basin: 'San Juan Basin (NW NM)', color: colors.warning },
      'Santa Fe': { basin: 'Raton & Other Basins', color: colors.secondary }
    };
    
    const info = districtInfo[district.district] || { basin: 'Unknown', color: colors.muted };
    
    return (
      <Card
        key={district.district}
        sx={{
          mb: 1.5,
          background: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          boxShadow: colors.boxShadow,
          cursor: 'pointer',
          '&:hover': { boxShadow: colors.boxShadowHover }
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${info.color}20`,
                flexShrink: 0
              }}
            >
              <AccountBalanceIcon sx={{ color: info.color }} />
            </Box>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                {district.district} District
              </Typography>
              <Typography variant="caption" sx={{ color: colors.muted }}>
                {info.basin}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={`${district.activeWells.toLocaleString()} Active`}
                  size="small"
                  sx={{ bgcolor: colors.chipBg, color: colors.success, fontWeight: 600 }}
                />
                <Chip
                  label={`${district.newPermits} New`}
                  size="small"
                  sx={{ bgcolor: colors.chipBgWarning, color: colors.warning, fontWeight: 600 }}
                />
                <Chip
                  label={district.primaryType}
                  size="small"
                  icon={district.primaryType === 'Oil' ? <OilBarrelIcon sx={{ fontSize: 14 }} /> : <LocalGasStationIcon sx={{ fontSize: 14 }} />}
                  sx={{ 
                    bgcolor: district.primaryType === 'Oil' ? colors.chipBg : colors.chipBgWarning, 
                    color: district.primaryType === 'Oil' ? colors.success : colors.warning,
                    '& .MuiChip-icon': { color: district.primaryType === 'Oil' ? colors.success : colors.warning }
                  }}
                />
              </Box>
            </Box>
            
            <IconButton
              size="small"
              onClick={() => setExpandedDistrict(isExpanded ? null : district.district)}
              sx={{ color: colors.muted }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${colors.borderLight}` }}>
              {/* Stats grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: colors.chipBgAlt, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: colors.success, fontWeight: 700 }}>{district.oilWells.toLocaleString()}</Typography>
                  <Typography variant="caption" sx={{ color: colors.muted }}>Oil Wells</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: colors.chipBgAlt, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: colors.warning, fontWeight: 700 }}>{district.gasWells.toLocaleString()}</Typography>
                  <Typography variant="caption" sx={{ color: colors.muted }}>Gas Wells</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: colors.chipBgAlt, borderRadius: 1 }}>
                  <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 700 }}>{district.injectionWells.toLocaleString()}</Typography>
                  <Typography variant="caption" sx={{ color: colors.muted }}>Inj/SWD</Typography>
                </Box>
              </Box>
              
              <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mb: 1 }}>
                {district.operatorCount} operators • {district.countyCount} counties • {district.activePercentage}% active rate
              </Typography>
              
              <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mb: 0.5 }}>
                Top Operators:
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {district.topOperators.slice(0, 3).map((op, i) => (
                  <Chip
                    key={i}
                    label={`${op.operator.substring(0, 20)}${op.operator.length > 20 ? '...' : ''} (${op.count})`}
                    size="small"
                    sx={{ fontSize: '0.65rem', height: 22, bgcolor: colors.chipBgAlt, color: colors.text }}
                  />
                ))}
              </Box>
              
              <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mt: 1.5, mb: 0.5 }}>
                Counties:
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text }}>
                {district.counties.join(', ')}
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    );
  };

  // Render Infrastructure card
  const renderInfrastructureContent = () => {
    if (!infrastructure) return null;
    
    return (
      <Box>
        <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
          Midstream infrastructure: Tank Batteries, Compressor Stations, Gas Plants
        </Typography>
        
        {/* Summary cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
          {infrastructure.summary.slice(0, 6).map((item) => (
            <Box
              key={item.category}
              sx={{
                p: 1.5,
                bgcolor: colors.cardBg,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: 2
              }}
            >
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                {item.category}
              </Typography>
              <Typography variant="h6" sx={{ color: colors.primary, fontWeight: 700 }}>
                {item.total.toLocaleString()}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip label={`${item.active} Active`} size="small" sx={{ bgcolor: colors.chipBg, color: colors.success, fontSize: '0.65rem', height: 20 }} />
                <Chip label={`${item.inactive} Inactive`} size="small" sx={{ bgcolor: colors.chipBgAlt, color: colors.muted, fontSize: '0.65rem', height: 20 }} />
              </Box>
            </Box>
          ))}
        </Box>
        
        {/* By District */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
          Infrastructure by District
        </Typography>
        {infrastructure.byDistrict.map((d) => (
          <Card
            key={d.district}
            sx={{
              mb: 1,
              bgcolor: colors.cardBg,
              border: `1px solid ${colors.borderColor}`,
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                {d.district || 'Unknown'} District
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                <Chip label={`TB: ${d.tankBatteries}`} size="small" sx={{ bgcolor: colors.chipBg, color: colors.success, fontSize: '0.65rem', height: 20 }} />
                <Chip label={`CS: ${d.compressorStations}`} size="small" sx={{ bgcolor: colors.chipBgPrimary, color: colors.primary, fontSize: '0.65rem', height: 20 }} />
                <Chip label={`GP: ${d.gasPlants}`} size="small" sx={{ bgcolor: colors.chipBgWarning, color: colors.warning, fontSize: '0.65rem', height: 20 }} />
                <Chip label={`FS: ${d.flareStacks}`} size="small" sx={{ bgcolor: colors.chipBgSecondary, color: colors.secondary, fontSize: '0.65rem', height: 20 }} />
              </Box>
            </CardContent>
          </Card>
        ))}
        
        {/* Top Operators */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
          Top Operators by Facilities
        </Typography>
        {infrastructure.topOperators.slice(0, 10).map((op, i) => (
          <Box
            key={op.operatorName}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              borderBottom: `1px solid ${colors.borderLight}`
            }}
          >
            <Typography variant="caption" sx={{ color: colors.muted, width: 20 }}>#{i + 1}</Typography>
            <Typography variant="caption" sx={{ color: colors.text, flex: 1 }}>{op.operatorName}</Typography>
            <Chip label={op.facilityCount} size="small" sx={{ bgcolor: colors.chipBgPrimary, color: colors.primary, fontSize: '0.7rem', height: 20 }} />
          </Box>
        ))}
        
        {/* Sample navigable facilities */}
        {infrastructure.sampleFacilities.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
              Navigate to Facilities
            </Typography>
            {infrastructure.sampleFacilities.slice(0, 8).map((f) => (
              <Card
                key={f.id}
                onClick={() => handleNavigateToZone(f.latitude, f.longitude)}
                sx={{
                  mb: 1,
                  bgcolor: colors.cardBg,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { boxShadow: colors.boxShadowHover }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FactoryIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 600, display: 'block' }}>
                      {f.name?.substring(0, 40) || 'Unknown'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.muted, fontSize: '0.65rem' }}>
                      {f.type} • {f.operator?.substring(0, 25)}
                    </Typography>
                  </Box>
                  <NavigationIcon sx={{ color: colors.success, fontSize: 18 }} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    );
  };

  // Render Land Type Analysis
  const renderLandTypeContent = () => {
    if (!landType) return null;
    
    return (
      <Box>
        <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
          Federal (BLM) vs State (Trust Land) vs Fee/Private well distribution
        </Typography>
        
        {/* Overall breakdown */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
          {landType.overall.map((lt) => (
            <Box
              key={lt.landType}
              sx={{
                p: 1.5,
                bgcolor: colors.cardBg,
                border: `1px solid ${colors.borderColor}`,
                borderRadius: 2,
                textAlign: 'center'
              }}
            >
              <Typography variant="caption" sx={{ color: colors.muted }}>{lt.landType}</Typography>
              <Typography variant="h6" sx={{ 
                color: lt.landType === 'Federal' ? colors.warning : lt.landType === 'State' ? colors.secondary : colors.success, 
                fontWeight: 700 
              }}>
                {lt.totalWells.toLocaleString()}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text }}>
                {lt.activeWells.toLocaleString()} active
              </Typography>
            </Box>
          ))}
        </Box>
        
        {/* By County */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
          Land Type by County
        </Typography>
        {landType.byCounty.slice(0, 12).map((c) => (
          <Card
            key={c.county}
            sx={{
              mb: 1,
              bgcolor: colors.cardBg,
              border: `1px solid ${colors.borderColor}`,
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  {c.county}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.muted }}>
                  {c.total.toLocaleString()} wells
                </Typography>
              </Box>
              {/* Progress bar */}
              <Box sx={{ display: 'flex', height: 8, borderRadius: 1, overflow: 'hidden', mb: 0.5 }}>
                <Box sx={{ width: `${c.federalPct}%`, bgcolor: colors.warning }} />
                <Box sx={{ width: `${c.statePct}%`, bgcolor: colors.secondary }} />
                <Box sx={{ width: `${c.feePct}%`, bgcolor: colors.success }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, fontSize: '0.65rem' }}>
                <Typography variant="caption" sx={{ color: colors.warning }}>Fed: {c.federalPct}%</Typography>
                <Typography variant="caption" sx={{ color: colors.secondary }}>State: {c.statePct}%</Typography>
                <Typography variant="caption" sx={{ color: colors.success }}>Fee: {c.feePct}%</Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
        
        {/* Top Federal Operators */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
          Top Operators on Federal Land
        </Typography>
        {landType.topFederalOperators.slice(0, 8).map((op, i) => (
          <Box
            key={op.operator}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              borderBottom: `1px solid ${colors.borderLight}`
            }}
          >
            <Typography variant="caption" sx={{ color: colors.muted, width: 20 }}>#{i + 1}</Typography>
            <Typography variant="caption" sx={{ color: colors.text, flex: 1 }}>{op.operator}</Typography>
            <Chip label={`${op.federalWells || 0} wells`} size="small" sx={{ bgcolor: colors.chipBgWarning, color: colors.warning, fontSize: '0.7rem', height: 20 }} />
          </Box>
        ))}
        
        {/* Top State Operators */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
          Top Operators on State Land
        </Typography>
        {landType.topStateOperators.slice(0, 8).map((op, i) => (
          <Box
            key={op.operator}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              borderBottom: `1px solid ${colors.borderLight}`
            }}
          >
            <Typography variant="caption" sx={{ color: colors.muted, width: 20 }}>#{i + 1}</Typography>
            <Typography variant="caption" sx={{ color: colors.text, flex: 1 }}>{op.operator}</Typography>
            <Chip label={`${op.stateWells || 0} wells`} size="small" sx={{ bgcolor: colors.chipBgSecondary, color: colors.secondary, fontSize: '0.7rem', height: 20 }} />
          </Box>
        ))}
      </Box>
    );
  };

  // Render Horizontal Well Stats
  const renderHorizontalContent = () => {
    if (!horizontal) return null;
    
    const horizTotal = horizontal.overall.find(o => o.wellOrientation === 'Horizontal');
    const vertTotal = horizontal.overall.find(o => o.wellOrientation === 'Vertical/Other');
    
    return (
      <Box>
        <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
          Horizontal vs Vertical wells • Horizontal drilling trends
        </Typography>
        
        {/* Overall comparison */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: colors.cardBg, border: `1px solid ${colors.borderColor}`, borderRadius: 2, textAlign: 'center' }}>
            <TimelineIcon sx={{ color: colors.primary, fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: colors.primary, fontWeight: 700 }}>
              {(horizTotal?.totalWells || 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.muted }}>Horizontal Wells</Typography>
            <Typography variant="caption" sx={{ color: colors.success, display: 'block' }}>
              {horizTotal?.activeWells || 0} active
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: colors.cardBg, border: `1px solid ${colors.borderColor}`, borderRadius: 2, textAlign: 'center' }}>
            <OilBarrelIcon sx={{ color: colors.muted, fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: colors.text, fontWeight: 700 }}>
              {(vertTotal?.totalWells || 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.muted }}>Vertical/Other</Typography>
            <Typography variant="caption" sx={{ color: colors.success, display: 'block' }}>
              {vertTotal?.activeWells || 0} active
            </Typography>
          </Box>
        </Box>
        
        {/* By Operator */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
          Top Horizontal Drillers
        </Typography>
        {horizontal.byOperator.slice(0, 12).map((op, i) => (
          <Box
            key={op.operator}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              borderBottom: `1px solid ${colors.borderLight}`
            }}
          >
            <Typography variant="caption" sx={{ color: colors.muted, width: 20 }}>#{i + 1}</Typography>
            <Typography variant="caption" sx={{ color: colors.text, flex: 1 }}>{op.operator}</Typography>
            <Chip label={`${op.horizontalWells} H-wells`} size="small" sx={{ bgcolor: colors.chipBgPrimary, color: colors.primary, fontSize: '0.7rem', height: 20 }} />
          </Box>
        ))}
        
        {/* By County */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
          Horizontal Activity by County
        </Typography>
        {horizontal.byCounty.slice(0, 10).map((c) => (
          <Card
            key={c.county}
            sx={{
              mb: 1,
              bgcolor: colors.cardBg,
              border: `1px solid ${colors.borderColor}`,
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  {c.county}
                </Typography>
                <Chip label={`${c.horizontalPct}% H`} size="small" sx={{ bgcolor: colors.chipBgPrimary, color: colors.primary, fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
              </Box>
              <Box sx={{ display: 'flex', height: 6, borderRadius: 1, overflow: 'hidden', bgcolor: colors.chipBgAlt }}>
                <Box sx={{ width: `${c.horizontalPct}%`, bgcolor: colors.primary }} />
              </Box>
              <Typography variant="caption" sx={{ color: colors.muted, mt: 0.5, display: 'block' }}>
                {c.horizontal} horizontal • {c.vertical} vertical
              </Typography>
            </CardContent>
          </Card>
        ))}
        
        {/* Sample navigable horizontal wells */}
        {horizontal.sampleWells.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
              Navigate to Horizontal Wells
            </Typography>
            {horizontal.sampleWells.slice(0, 6).map((w) => (
              <Card
                key={w.id}
                onClick={() => handleNavigateToZone(w.latitude, w.longitude)}
                sx={{
                  mb: 1,
                  bgcolor: colors.cardBg,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { boxShadow: colors.boxShadowHover }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon sx={{ color: colors.primary, fontSize: 20 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 600, display: 'block' }}>
                      {w.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.muted, fontSize: '0.65rem' }}>
                      {w.operator?.substring(0, 25)} • {w.county}
                    </Typography>
                  </Box>
                  <NavigationIcon sx={{ color: colors.success, fontSize: 18 }} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    );
  };

  // Render Plugged Well Analysis
  const renderPluggedContent = () => {
    if (!plugged) return null;
    
    const released = plugged.overall.find(o => o.status === 'Plugged (site released)');
    const notReleased = plugged.overall.find(o => o.status === 'Plugged (not released)');
    const totalPlugged = (released?.count || 0) + (notReleased?.count || 0);
    const releaseRate = totalPlugged > 0 ? Math.round(((released?.count || 0) / totalPlugged) * 100) : 0;
    
    return (
      <Box>
        <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
          Plugged well environmental status • Site released vs pending remediation
        </Typography>
        
        {/* Overall stats */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
          <Box sx={{ p: 1.5, bgcolor: colors.cardBg, border: `1px solid ${colors.borderColor}`, borderRadius: 2, textAlign: 'center' }}>
            <VerifiedIcon sx={{ color: colors.success, fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: colors.success, fontWeight: 700 }}>
              {(released?.count || 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.muted }}>Site Released</Typography>
            <Typography variant="caption" sx={{ color: colors.success, display: 'block' }}>
              Remediation complete
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, bgcolor: colors.cardBg, border: `1px solid ${colors.borderColor}`, borderRadius: 2, textAlign: 'center' }}>
            <BlockIcon sx={{ color: colors.danger, fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: colors.danger, fontWeight: 700 }}>
              {(notReleased?.count || 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.muted }}>Not Released</Typography>
            <Typography variant="caption" sx={{ color: colors.warning, display: 'block' }}>
              Pending remediation
            </Typography>
          </Box>
        </Box>
        
        {/* Overall release rate */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: colors.cardBg, border: `1px solid ${colors.borderColor}`, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 0.5 }}>
            Overall Release Rate: {releaseRate}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={releaseRate}
            sx={{
              height: 8,
              borderRadius: 1,
              bgcolor: colors.chipBgAlt,
              '& .MuiLinearProgress-bar': { bgcolor: colors.success }
            }}
          />
        </Box>
        
        {/* By Operator */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
          Operators with Unreleased Wells
        </Typography>
        {plugged.byOperator.filter(o => o.notReleased > 0).slice(0, 12).map((op) => (
          <Card
            key={op.operator}
            sx={{
              mb: 1,
              bgcolor: colors.cardBg,
              border: `1px solid ${colors.borderColor}`,
              borderRadius: 2
            }}
          >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.8rem' }}>
                  {op.operator}
                </Typography>
                <Chip 
                  label={`${op.releaseRate}% released`} 
                  size="small" 
                  sx={{ 
                    bgcolor: op.releaseRate > 70 ? colors.chipBg : op.releaseRate > 40 ? colors.chipBgWarning : colors.errorBg, 
                    color: op.releaseRate > 70 ? colors.success : op.releaseRate > 40 ? colors.warning : colors.danger,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    height: 20
                  }} 
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`${op.released} released`} size="small" sx={{ bgcolor: colors.chipBg, color: colors.success, fontSize: '0.65rem', height: 18 }} />
                <Chip label={`${op.notReleased} pending`} size="small" sx={{ bgcolor: colors.errorBg, color: colors.danger, fontSize: '0.65rem', height: 18 }} />
                <Typography variant="caption" sx={{ color: colors.muted, alignSelf: 'center' }}>
                  of {op.totalWells.toLocaleString()} total
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
        
        {/* By County */}
        <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
          Plugged Wells by County
        </Typography>
        {plugged.byCounty.slice(0, 10).map((c) => (
          <Box
            key={c.county}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 0.75,
              borderBottom: `1px solid ${colors.borderLight}`
            }}
          >
            <Typography variant="caption" sx={{ color: colors.text, flex: 1 }}>{c.county}</Typography>
            <Chip label={`${c.released} ✓`} size="small" sx={{ bgcolor: colors.chipBg, color: colors.success, fontSize: '0.65rem', height: 18 }} />
            <Chip label={`${c.notReleased} ⏳`} size="small" sx={{ bgcolor: colors.errorBg, color: colors.danger, fontSize: '0.65rem', height: 18 }} />
          </Box>
        ))}
        
        {/* Sample unreleased wells */}
        {plugged.unreleasedWells.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mt: 2, mb: 1 }}>
              Unreleased Well Locations
            </Typography>
            {plugged.unreleasedWells.slice(0, 6).map((w) => (
              <Card
                key={w.id}
                onClick={() => handleNavigateToZone(w.latitude, w.longitude)}
                sx={{
                  mb: 1,
                  bgcolor: colors.cardBg,
                  border: `1px solid ${colors.borderColor}`,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { boxShadow: colors.boxShadowHover }
                }}
              >
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BlockIcon sx={{ color: colors.danger, fontSize: 20 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: colors.textPrimary, fontWeight: 600, display: 'block' }}>
                      {w.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.muted, fontSize: '0.65rem' }}>
                      {w.operator?.substring(0, 25)} • {w.county}
                    </Typography>
                  </Box>
                  <NavigationIcon sx={{ color: colors.warning, fontSize: 18 }} />
                </CardContent>
              </Card>
            ))}
          </>
        )}
      </Box>
    );
  };

  // Render Weekly Activity content (Tab 10)
  const renderWeeklyActivityContent = () => {
    if (!weeklyActivity) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ color: colors.muted }}>
            No weekly activity data available
          </Typography>
        </Box>
      );
    }

    const { stats, permits: weeklyPermits, completions, incidents, rigs, weekDate } = weeklyActivity;

    const renderActivityCard = (item: WeeklyActivityItem, icon: React.ReactNode, chipColor: string) => (
      <Card
        key={item.id}
        onClick={() => item.latitude && item.longitude ? handleNavigateToZone(item.latitude, item.longitude) : null}
        sx={{
          mb: 1,
          bgcolor: colors.cardBg,
          border: `1px solid ${colors.borderColor}`,
          borderRadius: 2,
          cursor: item.latitude && item.longitude ? 'pointer' : 'default',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': item.latitude && item.longitude ? { 
            transform: 'scale(1.02)',
            boxShadow: colors.boxShadowHover 
          } : {}
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {icon}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.85rem' }}>
                {item.wellName}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.muted, display: 'block' }}>
                {item.operator?.substring(0, 30)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={item.county}
                  size="small"
                  sx={{ 
                    bgcolor: colors.chipBgPrimary, 
                    color: colors.primary, 
                    fontSize: '0.65rem', 
                    height: 20 
                  }}
                />
                {item.apiNumber && (
                  <Chip
                    label={item.apiNumber}
                    size="small"
                    sx={{ 
                      bgcolor: colors.chipBgAlt, 
                      color: colors.muted, 
                      fontSize: '0.65rem',
                      height: 20
                    }}
                  />
                )}
                {item.formation && (
                  <Chip
                    label={item.formation}
                    size="small"
                    sx={{ 
                      bgcolor: chipColor, 
                      color: colors.success, 
                      fontSize: '0.65rem',
                      height: 20
                    }}
                  />
                )}
              </Box>
              {item.description && (
                <Typography variant="caption" sx={{ color: colors.warning, display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                  {item.description}
                </Typography>
              )}
            </Box>
            {item.latitude && item.longitude && (
              <IconButton size="small" sx={{ color: colors.primary }}>
                <NavigationIcon sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </CardContent>
      </Card>
    );

    return (
      <Box>
        {/* Week header */}
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          bgcolor: colors.gradientPrimary, 
          borderRadius: 2, 
          border: `1px solid ${colors.borderColor}` 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <UpdateIcon sx={{ color: colors.primary }} />
              <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 700 }}>
                Weekly Activity Report
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: colors.muted }}>
              Week of {weekDate}
            </Typography>
          </Box>
          
          {/* Stats summary */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Chip
              icon={<NewReleasesIcon sx={{ fontSize: 16 }} />}
              label={`${stats?.permits || 0} Permits`}
              size="small"
              sx={{ 
                bgcolor: colors.chipBgWarning, 
                color: colors.warning,
                '& .MuiChip-icon': { color: colors.warning }
              }}
            />
            <Chip
              icon={<BuildIcon sx={{ fontSize: 16 }} />}
              label={`${stats?.completions || 0} Completions`}
              size="small"
              sx={{ 
                bgcolor: colors.chipBg, 
                color: colors.success,
                '& .MuiChip-icon': { color: colors.success }
              }}
            />
            <Chip
              icon={<OilBarrelIcon sx={{ fontSize: 16 }} />}
              label={`${stats?.rigMovements || 0} Rig Moves`}
              size="small"
              sx={{ 
                bgcolor: colors.chipBgPrimary, 
                color: colors.primary,
                '& .MuiChip-icon': { color: colors.primary }
              }}
            />
            {(stats?.incidents || 0) > 0 && (
              <Chip
                icon={<ReportProblemIcon sx={{ fontSize: 16 }} />}
                label={`${stats.incidents} Incidents`}
                size="small"
                sx={{ 
                  bgcolor: colors.errorBg, 
                  color: colors.danger,
                  '& .MuiChip-icon': { color: colors.danger }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Section tabs */}
        <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
          {[
            { key: 'permits', label: 'Permits', count: weeklyPermits?.length || 0, color: colors.warning },
            { key: 'completions', label: 'Completions', count: completions?.length || 0, color: colors.success },
            { key: 'rigs', label: 'Rig Activity', count: rigs?.length || 0, color: colors.primary },
            { key: 'incidents', label: 'Incidents', count: incidents?.length || 0, color: colors.danger }
          ].map(section => (
            <Chip
              key={section.key}
              label={`${section.label} (${section.count})`}
              size="small"
              onClick={() => setExpandedWeeklySection(section.key)}
              sx={{
                bgcolor: expandedWeeklySection === section.key ? section.color : colors.chipBgAlt,
                color: expandedWeeklySection === section.key ? '#fff' : colors.muted,
                fontWeight: expandedWeeklySection === section.key ? 600 : 400,
                cursor: 'pointer',
                '&:hover': { opacity: 0.9 }
              }}
            />
          ))}
        </Box>

        {/* Permits section */}
        {expandedWeeklySection === 'permits' && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <NewReleasesIcon sx={{ color: colors.warning, fontSize: 20 }} />
              New Drilling Permits
            </Typography>
            {weeklyPermits && weeklyPermits.length > 0 ? (
              weeklyPermits.map(item => renderActivityCard(
                item,
                <NewReleasesIcon sx={{ color: colors.warning, fontSize: 24, mt: 0.5 }} />,
                colors.chipBgWarning
              ))
            ) : (
              <Typography variant="caption" sx={{ color: colors.muted }}>No new permits this week</Typography>
            )}
          </Box>
        )}

        {/* Completions section */}
        {expandedWeeklySection === 'completions' && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BuildIcon sx={{ color: colors.success, fontSize: 20 }} />
              Well Completions
            </Typography>
            {completions && completions.length > 0 ? (
              completions.map(item => renderActivityCard(
                item,
                <BuildIcon sx={{ color: colors.success, fontSize: 24, mt: 0.5 }} />,
                colors.chipBg
              ))
            ) : (
              <Typography variant="caption" sx={{ color: colors.muted }}>No completions this week</Typography>
            )}
          </Box>
        )}

        {/* Rig activity section */}
        {expandedWeeklySection === 'rigs' && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <OilBarrelIcon sx={{ color: colors.primary, fontSize: 20 }} />
              Rig Movements
            </Typography>
            {rigs && rigs.length > 0 ? (
              rigs.map(item => renderActivityCard(
                item,
                <OilBarrelIcon sx={{ color: colors.primary, fontSize: 24, mt: 0.5 }} />,
                colors.chipBgPrimary
              ))
            ) : (
              <Typography variant="caption" sx={{ color: colors.muted }}>No rig movements this week</Typography>
            )}
          </Box>
        )}

        {/* Incidents section */}
        {expandedWeeklySection === 'incidents' && (
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <ReportProblemIcon sx={{ color: colors.danger, fontSize: 20 }} />
              Reported Incidents
            </Typography>
            {incidents && incidents.length > 0 ? (
              incidents.map(item => renderActivityCard(
                item,
                <WarningAmberIcon sx={{ color: colors.danger, fontSize: 24, mt: 0.5 }} />,
                colors.errorBg
              ))
            ) : (
              <Typography variant="caption" sx={{ color: colors.muted }}>No incidents reported this week</Typography>
            )}
          </Box>
        )}

        {/* Top operators this week */}
        {stats?.topOperators && stats.topOperators.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}>
              Most Active Operators This Week
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {stats.topOperators.map((op, i) => (
                <Chip
                  key={op.operator}
                  label={`#${i + 1} ${op.operator?.substring(0, 20)}: ${op.count}`}
                  size="small"
                  sx={{
                    bgcolor: i < 3 ? colors.chipBgSecondary : colors.chipBgAlt,
                    color: i < 3 ? colors.secondary : colors.muted,
                    fontSize: '0.7rem'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          bgcolor: colors.dark,
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle
        sx={{
          background: colors.gradientPrimary,
          borderBottom: `1px solid ${colors.borderLight}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ color: colors.primary }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.textPrimary }}>
            Active Operations
          </Typography>
          <Chip
            icon={<VerifiedIcon sx={{ fontSize: 14 }} />}
            label="Real Data"
            size="small"
            sx={{
              ml: 1,
              bgcolor: colors.chipBg,
              color: colors.success,
              '& .MuiChip-icon': { color: colors.success }
            }}
          />
        </Box>
        <IconButton onClick={() => { telegram.hapticFeedback('light'); onClose(); }} sx={{ color: colors.textPrimary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Box sx={{ borderBottom: `1px solid ${colors.borderLight}` }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              color: colors.muted,
              fontWeight: 600,
              minWidth: 'auto',
              px: 2,
              '&.Mui-selected': { color: colors.primary }
            },
            '& .MuiTabs-indicator': { bgcolor: colors.primary },
            '& .MuiTabs-scrollButtons': { color: colors.muted }
          }}
        >
          <Tab 
            icon={<BusinessIcon sx={{ fontSize: 18 }} />} 
            label="Operators" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<LocationOnIcon sx={{ fontSize: 18 }} />} 
            label="Counties" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<WhatshotIcon sx={{ fontSize: 18 }} />} 
            label="Hot Zones" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<CategoryIcon sx={{ fontSize: 18 }} />} 
            label="Well Types" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<NewReleasesIcon sx={{ fontSize: 18 }} />} 
            label="New Permits" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<AccountBalanceIcon sx={{ fontSize: 18 }} />} 
            label="Districts" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<FactoryIcon sx={{ fontSize: 18 }} />} 
            label="Infrastructure" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<TerrainIcon sx={{ fontSize: 18 }} />} 
            label="Land Type" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<TimelineIcon sx={{ fontSize: 18 }} />} 
            label="Horizontal" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<BlockIcon sx={{ fontSize: 18 }} />} 
            label="Plugged" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
          <Tab 
            icon={<EventIcon sx={{ fontSize: 18 }} />} 
            label="Weekly" 
            iconPosition="start"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>
      
      <DialogContent sx={{ p: 2, bgcolor: colors.dark }}>
        {/* Data source disclaimer */}
        <Alert
          severity="info"
          icon={<VerifiedIcon />}
          sx={{
            mb: 2,
            bgcolor: colors.alertBg,
            border: `1px solid ${colors.alertBorder}`,
            color: colors.text,
            '& .MuiAlert-icon': { color: colors.primary }
          }}
        >
          <Typography variant="caption">
            Data source: New Mexico Oil Conservation Division (OCD). 
            {activeTab === 0 && ` Showing operators with most active wells.`}
            {activeTab === 1 && ` Showing active well counts by county.`}
            {activeTab === 2 && ` Showing operator activity clusters.`}
            {activeTab === 3 && ` Showing oil/gas/injection well distribution.`}
            {activeTab === 4 && ` Showing newly permitted wells (future drilling locations).`}
            {activeTab === 5 && ` Comparing NM OCD districts: Hobbs, Artesia, Aztec, Santa Fe.`}
            {activeTab === 6 && ` Midstream infrastructure: Tank Batteries, Compressor Stations, Gas Plants.`}
            {activeTab === 7 && ` Federal (BLM) vs State vs Fee/Private well distribution.`}
            {activeTab === 8 && ` Horizontal drilling activity - modern unconventional wells.`}
            {activeTab === 9 && ` Plugged well remediation status - environmental tracking.`}
            {activeTab === 10 && ` Weekly activity report - new permits, completions, incidents.`}
          </Typography>
        </Alert>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CyberSpinner size={50} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ bgcolor: colors.errorBg, color: colors.danger }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Tab 0: Top Operators */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
                  {operators.length} operators with active wells in New Mexico
                </Typography>
                {operators.map((op, i) => renderOperatorCard(op, i))}
              </Box>
            )}
            
            {/* Tab 1: Counties */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
                  {counties.length} counties with well data
                </Typography>
                {counties.map(renderCountyCard)}
              </Box>
            )}
            
            {/* Tab 2: Hot Zones */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
                  {hotZones.length} active drilling clusters identified
                </Typography>
                {hotZones.map((zone, i) => renderHotZoneCard(zone, i))}
              </Box>
            )}
            
            {/* Tab 3: Well Types */}
            {activeTab === 3 && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
                  Oil vs Gas vs Injection well distribution by county
                </Typography>
                {wellTypes.map(renderWellTypeCard)}
              </Box>
            )}
            
            {/* Tab 4: New Permits */}
            {activeTab === 4 && (
              <Box>
                {permitSummary.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" sx={{ color: colors.muted, display: 'block', mb: 1 }}>
                      New permits by county (future drilling locations):
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {permitSummary.map(s => (
                        <Chip
                          key={s.county}
                          label={`${s.county}: ${s.permitCount}`}
                          size="small"
                          sx={{ bgcolor: colors.chipBgWarning, color: colors.warning }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
                  {permits.length} newly permitted wells • Tap to navigate
                </Typography>
                {permits.map(renderPermitCard)}
              </Box>
            )}
            
            {/* Tab 5: Districts */}
            {activeTab === 5 && (
              <Box>
                <Typography variant="caption" sx={{ color: colors.muted, mb: 2, display: 'block' }}>
                  4 NM OCD Districts covering different basins
                </Typography>
                {districts.map(renderDistrictCard)}
              </Box>
            )}
            
            {/* Tab 6: Infrastructure */}
            {activeTab === 6 && renderInfrastructureContent()}
            
            {/* Tab 7: Land Type */}
            {activeTab === 7 && renderLandTypeContent()}
            
            {/* Tab 8: Horizontal */}
            {activeTab === 8 && renderHorizontalContent()}
            
            {/* Tab 9: Plugged */}
            {activeTab === 9 && renderPluggedContent()}
            
            {/* Tab 10: Weekly Activity */}
            {activeTab === 10 && renderWeeklyActivityContent()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ActiveOperations;
